import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ObjectAttachmentModel } from '@base/modules/rest/object-attachment/object-attachment.model';
import { TipPrilogaModel } from '@base/modules/rest/object-attachment/tip-priloga.model';
import { FileUploadingModel } from '@base/modules/rest/common/model/file-uploading.model';
import { map } from 'rxjs/operators';
import { generateUUID } from '@base/utils/uuid';
import { ObjectRestServiceCreator } from '@base/modules/rest/object/object-rest-service-creator';
import { createFieldFilter } from '@views/secured/object/utils/composite-filters.util';
import { FilterOperationEnum } from '@shared/components/filter/enums/filter-operation.enum';
import { AppConfiguration } from '../../../../app-configuration';
import { FileRestUtilsService } from '../common/file-rest-utils.service';
import { ObjectNameEnum } from '../master-data-history/model/enums/object-name.enum';
import { ObjectCompositeFilterModel } from '../object/model/object-composite-filter.model';
import { ObjectFindRequestModel } from '../object/model/object-find-request.model';
import { PaginationResponseModel } from '../../../model/common/pagination-response.model';

@Injectable()
export class ObjectAttachmentRestService<E = any> {
  controllerUrl: string;

  constructor(private httpClient: HttpClient,
              private appConfiguration: AppConfiguration,
              private fileRestUtilsService: FileRestUtilsService,
              private objectRestServiceCreator: ObjectRestServiceCreator) {
    this.controllerUrl = this.appConfiguration.serverApiUrl + '/secured/object-attachment';
  }

  uploadFile<T>(payload: {
    objectName: string;
    recordId: number;
    file: File;
    attachment?: ObjectAttachmentModel;
  }): Observable<FileUploadingModel<T>> {
    const data: FormData = new FormData();
    data.append('file', payload.file);
    data.append('objectAttachment', JSON.stringify(payload.attachment));
    const guid = generateUUID();
    return this.fileRestUtilsService.uploadFileRequest(`${this.controllerUrl}/${payload.objectName}/${payload.recordId}/upload`, data, payload.file.size)
      .pipe(
        map((res: FileUploadingModel<T>) => {
          res._guid = guid;
          return res;
        })
      );
  }

  updateAttachment(payload: { attachment: ObjectAttachmentModel }): Observable<any> {
    return this.httpClient.put(`${this.controllerUrl}/${payload.attachment?.id}`, payload.attachment);
  }

  downloadAttachmentUrl(payload: { attachmentId: number }): string {
    return `${this.controllerUrl}/${payload.attachmentId}/download`;
  }

  viewAttachmentUrl(payload: { attachmentId: number }): string {
    return `${this.controllerUrl}/${payload.attachmentId}/view`;
  }

  find(data: ObjectFindRequestModel): Observable<PaginationResponseModel<E>> {
    return this.httpClient.post<PaginationResponseModel<E>>(`${this.controllerUrl}/find`, data);
  }

  getAttachment(payload: { id: number }): Observable<ObjectAttachmentModel> {
    return this.httpClient.get<ObjectAttachmentModel>(`${this.controllerUrl}/attachment/${payload.id}`);
  }

  delete(payload: { id: number; objectName: string }): Observable<void> {
    return this.httpClient.delete<void>(`${this.controllerUrl}/${payload.objectName}/${payload.id}`);
  }

  getTipPrilogaByBbsObjectName(objectName: string): Observable<TipPrilogaModel> {
    const compositeFilter: ObjectCompositeFilterModel = {
      filters: [createFieldFilter('item1', 'TipPriloga.BbsObject', 'name', objectName, FilterOperationEnum.EQUALS)],
      expression: 'item1',
    };


    return this.objectRestServiceCreator.create(ObjectNameEnum.TipPriloga)
      .find({
        page: {
          page: 0,
          size: 1,
        },
        compositeFilters: compositeFilter,
      })
      .pipe(
        map(val => val.content[0] as TipPrilogaModel)
      );
  }
}
